<template>
  <DataTile
    v-model:expanded="isExpanded"
    :data="claimData"
    :subtitle="claimSubtitle"
    :title="t('claims.lossType.' + claim.lossType)"
    icon-class="icon-schadenmeldung"
  >
    <template #footer>
      <div class="footer-cta footer-cta--left">
        <CustomButton
          @click="goToSchadenassistent"
          :button-color="ButtonColor.SECONDARY"
          data-testid="goToSchadenassistentButton"
        >
          {{ t('goToSchadenassistenten') }}
        </CustomButton>
      </div>
    </template>
  </DataTile>
</template>

<script lang="ts" setup>
import { ButtonColor, CustomButton, DataTile } from 'de-zurich-ui-library'
import { computed, ref } from 'vue'
import type { Claim } from '@/types/Claim'
import { useI18n } from 'vue-i18n'
import { formatDate } from '@/utils/policy/PolicyUtils'
import { storeClaimsData } from '@/utils/utils'
import { isDa } from '@/utils/CommonUtils'

const { t } = useI18n()

interface Props {
  claim: Claim
  claimId?: string
  claimRole?: string
  defaultExpanded?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  claimId: '',
  claimRole: '',
  defaultExpanded: false
})

const isExpanded = ref(props.defaultExpanded)

// Subtitle: Schadennummer
const claimSubtitle = computed(() => {
  return props.claim.claimReference.claimId
})

// Data array for DataTile
const claimData = computed(() => {
  return [
    {
      label: t('claims.claimDetails.claimNumber'),
      value: props.claim.claimReference.claimId
    },
    {
      label: t('claims.claimDetails.claimDate'),
      value: formatDate(props.claim.lossDate)
    },
    {
      label: t('claims.claimDetails.policyNumber'),
      value: props.claim.policyNumber
    }
  ]
})

function goToSchadenassistent() {
  storeClaimsData(isDa() ? 'DA' : 'ZUR')

  const baseUrl: string =
    globalThis.ENV_CONFIG?.WEB_COMPONENTS?.CLAIMS_SCHADENASSISTENT || ''

  if (!baseUrl) {
    console.error('Missing CLAIMS_SCHADENASSISTENT URL in ENV_CONFIG')
    return
  }

  const claimId: string = props.claimId
  if (!claimId) {
    console.error('Missing claimId for Schadenassistent URL')
    return
  }

  const claimRole: string = props.claimRole
  if (!claimRole) {
    console.error('Missing claimRole for Schadenassistent URL')
    return
  }

  // URL wird zusammengebaut aus statischem Teil + claimId aus Response + claimRole aus Response + companyName aus Umgebung
  // Beispiel: https://uat-germany.jss-uat-de.zurich.com/de-de/services/schadenassistent/25-12345?claimRole=1
  const fullURL = `${baseUrl}/${claimId}?claimRole=${claimRole}`

  // Navigiere zum Schadenassistent mit fullURL in neuem Tab
  globalThis.open(fullURL, '_blank')
}
</script>

<style lang="scss" scoped>
// Styles are mostly handled by DataTile component
</style>
